import React, { useCallback, useMemo } from "react";
import { FCX } from "@models";
import { UseFormReturn } from "@hooks/useForm";
import { BodyUpdateSurvey, GetSurveyInitialDataResponse, SurveyDetailsSampleProvider } from "@core/api/useSurveys";
import ManagementSectionHeader from "@pages/SurveyDetails/components/ManagementSectionHeader";
import ProviderColumn from "@pages/SurveyDetails/components/ProviderColumn";
import cn from "classnames";
import { formatNumber } from "@core/utils/string";

const ProviderManagement: FCX<{
    form: UseFormReturn<BodyUpdateSurvey>;
    initialData: GetSurveyInitialDataResponse;
    isOpened: boolean;
    setIsOpened: (_value: boolean) => void;
}> = ({
    form: {
        state: {
            sampleProviders
        },
        actions,
    },
    initialData: { sampleProviders: initialSampleProviders },
    isOpened,
    setIsOpened,
}) => {
    const reset = useCallback(
        () => {
            actions.setFields({
                sampleProviders: initialSampleProviders?.map((ip) => {
                    const existingProvider = sampleProviders?.find((sp) => sp.sampleProviderId === ip.sampleProviderId);
                    return {
                        ...existingProvider,
                        percentage: ip.percentage,
                    };
                }),
            });
        },
        [actions, sampleProviders, initialSampleProviders]
    );

    const handleProviderChange = useCallback(<T extends keyof SurveyDetailsSampleProvider>(
        index: number,
        field: T,
        value: SurveyDetailsSampleProvider[T]
    ) => {
        if (!sampleProviders) return;

        sampleProviders[index][field] = value;

        // Update the form state with a deep clone of to trigger reactivity!
        actions.setField('sampleProviders', structuredClone(sampleProviders));
    }, [actions, sampleProviders]);

    const totalSampleShare = useMemo(() => {
        return +formatNumber((sampleProviders ?? []).reduce((sum: number, p) => sum + (p.percentage ?? 0) * 100, 0));
    }, [sampleProviders]);

    const providersColumns = useMemo(() => {
        return (sampleProviders ?? []).map((provider, idx) => {
            const initialProvider = initialSampleProviders?.find(p => p.sampleProviderId === provider.sampleProviderId);

            return provider && initialProvider ? (
                <React.Fragment key={provider.sampleProviderId}>
                    {idx > 0 && <div className="SurveyDetails__spacer" />}
                    <ProviderColumn provider={provider} initialProvider={initialProvider} index={idx} onChange={handleProviderChange} />
                </React.Fragment>
            ) : null;
        });
    }, [sampleProviders, initialSampleProviders, handleProviderChange]);

    return (
        <>
            <div className="Separator" />
            <ManagementSectionHeader
                heading="Provider Management"
                reset={reset}
                isOpened={isOpened}
                setIsOpened={setIsOpened}
            />
            {isOpened && (
                <div className="SurveyDetails__fielding-container" style={{ flexDirection: 'column' }}>
                    <div className="SurveyDetails__sample-share-header">
                        <span className={cn("SurveyDetails__sample-share", totalSampleShare !== 100 && 'error')}>
                            SAMPLE SHARE: {totalSampleShare}%
                        </span>
                    </div>
                    <div className="SurveyDetails__providers-grid">
                        {providersColumns}
                    </div>
                </div>
            )}
        </>
    );
}

export default ProviderManagement;